// src/services/homeAdminService.js
//
// One-shot reads for the admin home page's snippets (UI revamp). Each is the
// same read its page makes; homeAdmin.js turns the result into what's shown.
// Read-only.

import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import {
  getCoordinatorEnrollmentsPath,
  getCoordinatorScreeningsPath,
  getEmployeeCredentialsPath,
  getEnrollmentPeriodPath,
  getMonthlyEnrollmentGoalsPath,
  getMonthlyScreeningGoalsPath,
  getScreeningPeriodPath,
} from "./firestorePaths";
import { fetchCoordinators } from "./coordinatorService";
import { buildGoalHistory, goalFromDoc } from "./goalProgress";
import { PERIOD_TYPES, currentPeriodKey } from "./enrollmentPeriods";
import { goalSnapshot } from "./homeAdmin";

const rows = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

/** Staff credentials (the Notifications page's read). */
export const loadEmployeeCredentials = async () => rows(await getDocs(collection(db, getEmployeeCredentialsPath())));

// A period's per-coordinator counts ([{ name, count }]), as the CRC page reads them.
const readPeriod = async (path) => (await getDocs(collection(db, path))).docs.map((d) => ({ name: d.id, ...d.data() }));
const soft = (promise, fallback) => promise.catch(() => fallback);
const goalDoc = async (path, monthId) => {
  const snap = await getDoc(doc(db, path, monthId));
  return { exists: snap.exists(), data: snap.exists() ? snap.data() : undefined };
};

/**
 * The CRC page's Goal Progress circles for a month (the same records it reads
 * and writes, so an edit on either page shows on both). A screening read
 * failing never breaks the randomization circle, as on that page.
 */
export const loadGoalCircles = async (monthId) => {
  const [roster, enrollDocs, screenDocs, enrollGoalDoc, screenGoalDoc] = await Promise.all([
    fetchCoordinators(),
    readPeriod(getEnrollmentPeriodPath(monthId)),
    soft(readPeriod(getScreeningPeriodPath(monthId)), []),
    soft(goalDoc(getMonthlyEnrollmentGoalsPath(), monthId), { exists: false }),
    soft(goalDoc(getMonthlyScreeningGoalsPath(), monthId), { exists: false }),
  ]);
  return goalSnapshot({ roster, enrollDocs, screenDocs, enrollGoalDoc, screenGoalDoc });
};

/**
 * The Goal Progress history (last 12 months, both circles): the CRC page's
 * History, with the same reads and the same shared builder (buildGoalHistory).
 */
export const loadGoalHistory = async (names) => {
  const isMonth = (id) => /^\d{4}-\d{2}$/.test(id);
  const [eSnap, sSnap] = await Promise.all([
    getDocs(collection(db, getCoordinatorEnrollmentsPath())),
    soft(getDocs(collection(db, getCoordinatorScreeningsPath())), { docs: [] }),
  ]);
  const closedByMonth = {};
  eSnap.docs.forEach((d) => {
    const v = d.data() || {};
    if (isMonth(d.id) && v.closedAt) closedByMonth[d.id] = v;
  });
  const current = currentPeriodKey(PERIOD_TYPES.MONTH);
  const monthIds = [...new Set([current, ...eSnap.docs.map((d) => d.id), ...sSnap.docs.map((d) => d.id)])]
    .filter((id) => isMonth(id) && id <= current)
    .sort()
    .reverse()
    .slice(0, 12);
  const docsByMonth = { enrollments: {}, screenings: {} };
  const goalsByMonth = { enrollments: {}, screenings: {} };
  await Promise.all(
    monthIds.map(async (id) => {
      const [eDocs, sDocs, eGoal, sGoal] = await Promise.all([
        readPeriod(getEnrollmentPeriodPath(id)),
        soft(readPeriod(getScreeningPeriodPath(id)), []),
        goalDoc(getMonthlyEnrollmentGoalsPath(), id),
        soft(goalDoc(getMonthlyScreeningGoalsPath(), id), null),
      ]);
      docsByMonth.enrollments[id] = eDocs;
      docsByMonth.screenings[id] = sDocs;
      goalsByMonth.enrollments[id] = goalFromDoc("enrollments", eGoal.exists, eGoal.data);
      goalsByMonth.screenings[id] = sGoal ? goalFromDoc("screenings", sGoal.exists, sGoal.data) : null;
    })
  );
  return buildGoalHistory({ monthIds, docsByMonth, goalsByMonth, closedByMonth, names, currentMonthId: current });
};
